import React, { useState, useEffect } from "react";
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
  Typography
} from "@mui/material";
import { styled } from "@mui/system";

import { customTheme } from "./theme/CustomTheme";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CarouselRules } from "./components/CarouselRules";
import { FileUploadPanel } from "./components/FileUploadPanel";
import { InstructionsPanel } from "./components/InstructionsPanel";
import { ResultsPanel } from "./components/ResultsPanel";

import { incoseSlides } from "./constants/IncoseSlides";
import { useSessionStorage } from "./hooks/UseSessionStorage";
import { getPresignedUrl, uploadToS3, analyzePDF } from "./services/ApiService";

const NaranjaContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFCC80",
  borderRadius: "8px",
  margin: "1rem 0",
  padding: "2rem",
  [theme.breakpoints.down("sm")]: { padding: "1rem" }
}));

const PanelContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "2rem",
  marginTop: "1rem",
  [theme.breakpoints.down("sm")]: { flexDirection: "column" }
}));

const Panel = styled(Box)({ flex: 1, padding: "1rem" });

function App() {
  
  const [pdfFile, setPdfFile] = useState(null);
  const [s3Key, setS3Key] = useState("");
  const [resultados, setResultados] = useState([]);

  const [uploadStatus, setUploadStatus] = useState("idle");   // idle | uploading | uploaded | error
  const [analysisStatus, setAnalysisStatus] = useState("idle"); // idle | in-progress | done | error
  const [mensajeError, setMensajeError] = useState("");

  const { getUserId, setUserId, getPdfUrl, setPdfUrl } = useSessionStorage();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setPdfFile(file);
  };

  const handleUpload = async () => {
    setMensajeError("");
    if (!pdfFile) {
      setUploadStatus("error");
      setMensajeError("Debés seleccionar un archivo PDF.");
      return;
    }
    try {
      setUploadStatus("uploading");
      const { presignedPost, s3Key: key, userId } = await getPresignedUrl(
        pdfFile.name || "document.pdf"
      );

      if (!getUserId()) setUserId(userId);
      setS3Key(key);
      await uploadToS3(presignedPost, pdfFile);

      setUploadStatus("uploaded");
    } catch (err) {
      console.error(err);
      setUploadStatus("error");
      setMensajeError(err.message);
      return;
    }
  };

  const handleAnalizar = async () => {
    setMensajeError("");
    if (!getUserId() || !s3Key) {
      setAnalysisStatus("error");
      setMensajeError("Debe cargarse el PDF antes de analizar.");
      return;
    }
    try {
      setAnalysisStatus("in-progress");
      const data = await analyzePDF(getUserId(), s3Key);
      setPdfUrl(data.pdfUrl || "");
      setResultados(data.requerimientos || []);
      setAnalysisStatus("done");
    } catch (err) {
      console.error(err);
      setAnalysisStatus("error");
      setMensajeError(err.message);
    }
  };

  const handleDescargarPDF = () => {
    const url = getPdfUrl();
    if (url) window.open(url, "_blank");
  };

  useEffect(() => {
    console.debug("userId:", getUserId());
    console.debug("pdfUrl:", getPdfUrl());
  }, [getUserId, getPdfUrl]);

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <Header />

      <Container maxWidth="lg" sx={{ minHeight: "100vh", pb: 4 }}>
       
        <NaranjaContainer sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: "bold",
              color: "#333",
              fontSize: "2rem",
              textAlign: "center"
            }}
          >
            ¿Qué reglas se tendrán en cuenta para el análisis?
          </Typography>
          <CarouselRules slides={incoseSlides} />
        </NaranjaContainer>

        <NaranjaContainer>
          <PanelContainer>
            <Panel>
              <FileUploadPanel
                pdfFile={pdfFile}
                onFileChange={handleFileChange}
                onUpload={handleUpload}
                onAnalyze={handleAnalizar}
                uploadStatus={uploadStatus}
                analysisStatus={analysisStatus}
                disableAnalyze={uploadStatus !== "uploaded"}
                mensajeError={mensajeError}
              />
            </Panel>
            <Panel>
              <InstructionsPanel />
            </Panel>
          </PanelContainer>
        </NaranjaContainer>

        {resultados.length > 0 && (
          <ResultsPanel resultados={resultados} onDownload={handleDescargarPDF} />
        )}
      </Container>

      <Footer />
    </ThemeProvider>
  );
}

export default App;